import React from 'react';
import Icon from 'flow/components/ui-components/Icon';
import useAppContext from 'flow/hooks/useAppContext';
import { UaePassError } from 'flow/types/UaePassLogin';
import { uaePassErrors } from 'flow/constants/uae-pass/uae-pass-constants';

interface Props {
    errorCode?: UaePassError | null;
    onBackToLogin: () => void;
}

interface ErrorFallback {
    title: string;
    description: string;
}

const RETRY_WITH_UAE_PASS = 'Please try to log in again using UAE PASS';

const ERROR_FALLBACKS: Record<UaePassError, ErrorFallback> = {
    [uaePassErrors.SERVICE_NOT_FOUND]: {
        title: 'Service unavailable',
        description:
            'UAE PASS service is currently unavailable. Please try again later.',
    },
    // A callback arriving without a code or state is a failed authentication, not a cancellation
    // — UAE Pass reports cancelling separately, as USER_CANCELLED.
    [uaePassErrors.MISSING_CODE]: {
        title: 'You are not authenticated to login',
        description: RETRY_WITH_UAE_PASS,
    },
    [uaePassErrors.MISSING_STATE]: {
        title: 'You are not authenticated to login',
        description: RETRY_WITH_UAE_PASS,
    },
    [uaePassErrors.USER_CANCELLED]: {
        title: 'Login via UAE PASS cancelled',
        description: RETRY_WITH_UAE_PASS,
    },
    [uaePassErrors.NOT_ELIGIBLE]: {
        title: 'Your UAE PASS profile is not verified. Visit UAE PASS app to upgrade your profile',
        description: 'Please get your UAEPASS profile verified.',
    },
    [uaePassErrors.NOT_AUTHENTICATED]: {
        title: 'You are not authenticated to login',
        description: RETRY_WITH_UAE_PASS,
    },
    [uaePassErrors.TIMEOUT]: {
        title: 'Your connection has timed out',
        description:
            'Please try again to login with UAEPASS or other login methods',
    },
    [uaePassErrors.GENERIC]: {
        title: 'Something went wrong',
        description:
            'Something went wrong during the login, please try again later.',
    },
};

const UaePassErrorState: React.FC<Props> = ({ errorCode, onBackToLogin }) => {
    const { i18next } = useAppContext();

    const resolvedCode: UaePassError = errorCode ?? uaePassErrors.GENERIC;
    const fallback = ERROR_FALLBACKS[resolvedCode];
    const title = i18next.t(
        `uaepass.error.${resolvedCode}.title`,
        fallback.title,
    );
    const description = i18next.t(
        `uaepass.error.${resolvedCode}.description`,
        fallback.description,
    );

    return (
        <div
            role="alert"
            className="flex flex-col items-center gap-6 text-center"
        >
            <h2 className="text-xl font-bold tracking-tight">{title}</h2>

            <p className="text-sm">{description}</p>

            <div className="flex flex-col items-center gap-3">
                <button
                    type="button"
                    onClick={onBackToLogin}
                    className="bg-main text-textOnBrand flex cursor-pointer items-center justify-center gap-2 rounded-full border-none px-6 py-3 text-sm font-semibold tracking-wide transition-all hover:opacity-90"
                >
                    <Icon
                        iconKey="arrow_back"
                        size="1rem"
                        className="rtl:-scale-x-100"
                    />
                    <span>
                        {i18next.t(
                            'uaepass.error.backToLogin',
                            'Back to login',
                        )}
                    </span>
                </button>
            </div>
        </div>
    );
};

export default UaePassErrorState;
